import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, CreditCard, Shield, ArrowRight, Loader2, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function SubscriptionPage() {
  const { user, fetchUser, getSubscriptionStatus, getTrialDaysRemaining } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && !polling) {
      setPolling(true);
      pollStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 5) {
      setPaymentStatus('timeout');
      setPolling(false);
      return;
    }
    try {
      const res = await paymentsAPI.status(sid);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPolling(false);
        fetchUser();
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        setPolling(false);
        return;
      }
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await paymentsAPI.checkout(origin);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar el pago');
      setLoading(false);
    }
  };

  const subStatus = getSubscriptionStatus();
  const daysLeft = getTrialDaysRemaining();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="subscription-page">
      <div className="text-center animate-fade-up">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="h-0.5 w-8 bg-trans-blue rounded-full" />
          <span className="text-xs font-sans font-semibold tracking-widest uppercase text-trans-blue">Suscripcion</span>
          <div className="h-0.5 w-8 bg-trans-pink rounded-full" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-dark">Plan de Suscripcion</h1>
        <p className="text-base font-sans text-gray-400 mt-3 max-w-lg mx-auto">
          Accede a todo el contenido de Archivos Trans y apoya la investigacion cientifica trans en America Latina.
        </p>
      </div>

      {/* Trial/Subscription Status Banner */}
      {user && subStatus === 'trial' && daysLeft > 0 && (
        <div className="mt-8 p-5 bg-blue-50 border border-blue-100 rounded-sm" data-testid="trial-banner">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-500 shrink-0" />
            <div>
              <p className="font-sans font-medium text-blue-800 text-sm">Periodo de prueba gratuita</p>
              <p className="font-sans text-blue-600 text-xs mt-0.5">
                Te quedan <strong>{daysLeft} dias</strong> de acceso gratuito. Suscribete para no perder acceso.
              </p>
            </div>
          </div>
        </div>
      )}
      {user && subStatus === 'expired' && (
        <div className="mt-8 p-5 bg-red-50 border border-red-100 rounded-sm" data-testid="expired-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="font-sans font-medium text-red-800 text-sm">Tu periodo de prueba ha expirado</p>
              <p className="font-sans text-red-600 text-xs mt-0.5">
                Suscribete para recuperar el acceso completo a todos los articulos y descargas.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment status */}
      {polling && (
        <div className="mt-8 p-6 bg-blue-50 border border-blue-100 rounded-sm text-center" data-testid="payment-polling">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
          <p className="font-sans text-blue-700">Verificando tu pago...</p>
        </div>
      )}
      {paymentStatus === 'success' && (
        <div className="mt-8 p-6 bg-green-50 border border-green-100 rounded-sm text-center" data-testid="payment-success">
          <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <h3 className="font-serif font-semibold text-green-700 text-lg">Pago exitoso</h3>
          <p className="font-sans text-green-600 text-sm mt-1">Tu suscripcion esta activa. Gracias por tu apoyo!</p>
          <Button onClick={() => navigate('/archivo')} className="mt-4 bg-green-600 hover:bg-green-700 font-sans" data-testid="go-to-archive-btn">
            Explorar articulos <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Plan card */}
      <div className="mt-12 max-w-md mx-auto">
        <div className="p-8 bg-white border border-gray-100 rounded-sm shadow-sm" data-testid="subscription-plan-card">
          <div className="text-center">
            <Badge className="bg-trans-blue/10 text-trans-blue border-trans-blue/20 font-sans mb-4">Plan Anual</Badge>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-sans font-bold text-slate-dark">$499</span>
              <span className="text-sm font-sans text-gray-400">MXN / ano</span>
            </div>
            <p className="text-sm font-sans text-gray-400 mt-2">6 meses gratis al registrarte</p>
          </div>

          <div className="mt-6 space-y-3">
            {[
              'Acceso completo a todos los articulos',
              'Archivo historico completo',
              'Descarga de PDFs',
              'Notificaciones de nuevas publicaciones',
              'Envio de manuscritos',
              'Apoyo a la investigacion trans'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-green-500" />
                </div>
                <span className="text-sm font-sans text-gray-600">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-8">
            {!user ? (
              <Button onClick={() => navigate('/registro')} className="w-full bg-slate-dark hover:bg-slate-dark/90 font-sans" data-testid="subscribe-register-btn">
                Registrarse <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : subStatus === 'active' ? (
              <div className="p-4 bg-green-50 border border-green-100 rounded-sm text-center" data-testid="active-sub-info">
                <Check className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="font-sans text-green-700 text-sm font-medium">Suscripcion activa</p>
                {user.subscription_end_date && (
                  <p className="font-sans text-green-600 text-xs mt-1">
                    Valida hasta {new Date(user.subscription_end_date).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
              </div>
            ) : (
              <Button onClick={handleCheckout} className="w-full bg-slate-dark hover:bg-slate-dark/90 font-sans" disabled={loading} data-testid="subscribe-pay-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</> : <><CreditCard className="w-4 h-4 mr-2" /> Suscribirme - $499 MXN</>}
              </Button>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-4 text-xs font-sans text-gray-400">
            <Shield className="w-3 h-3" /> Pago seguro con Stripe
          </div>
        </div>
      </div>
    </div>
  );
}
